import { useEffect, useRef } from "react"
import { useBaseMap } from "../BaseMap"
import { useHub } from "../../../core/Global"

interface IProps {
    stateId?: string
    /** Bring the whole state into view once its outline is in. */
    fit?: boolean
    color?: string
}

// Outlines already fetched, by state: they do not change, and OpenStreetMap's search asks to be
// spared repeat questions.
const outlines: Record<string, any> = {}

/**
 * A state's (or region's) border, drawn as an outline over the map. The state is looked up on
 * the hub, its shape on OpenStreetMap's Nominatim -- as the old map did.
 */
export default function StateOutlineLayer(props: IProps) {

    const { L, map } = useBaseMap()
    const hub = useHub()
    // Only the answer about the latest state is drawn.
    const requestId = useRef(0)

    useEffect(() => {
        if (!props.stateId) return
        const id = ++requestId.current
        let layer: any

        const draw = (geojson: any, fallback?: { lat: number, lng: number }) => {
            if (id !== requestId.current) return
            if (geojson) {
                layer = L.geoJSON(geojson, {
                    interactive: false,
                    style: { color: props.color ?? "#A02334", weight: 2, opacity: .7, fill: false }
                }).addTo(map)
                if (props.fit) map.fitBounds(layer.getBounds(), { padding: [20, 20] })
            }
            else if (props.fit && fallback) map.setView([fallback.lat, fallback.lng], 6)
        }

        const stateId = props.stateId
        if (outlines[stateId]) draw(outlines[stateId])
        else hub.Get("/maps/geocode/state/" + stateId, res => {
            if (!res.success || id !== requestId.current) return
            const address = res.data?.address
            const position = res.data?.position
            if (!address?.state) {
                draw(undefined, position)
                return
            }
            const q = `state=${encodeURIComponent(address.state)}&country=${encodeURIComponent(address.countryName ?? "usa")}`
            fetch(`https://nominatim.openstreetmap.org/search.php?${q}&polygon_geojson=1&format=jsonv2`)
                .then(r => r.json())
                .then(json => {
                    const geojson = json?.[0]?.geojson
                    if (geojson) outlines[stateId] = geojson
                    draw(geojson, position)
                })
                .catch(() => draw(undefined, position))
        })

        return () => { layer?.remove() }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, props.stateId])

    return null
}
